import React, { useState, useEffect } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { getPhotos } from "@/services/photoStorage";
import { Photo } from "@/types/photos";
import { downloadPhoto } from "@/utils/photoDownloader";
import SearchAndFilter from "@/components/gallery/SearchAndFilter";
import PhotoGrid from "@/components/gallery/PhotoGrid";
import LoadMoreButton from "@/components/gallery/LoadMoreButton";

const Gallery = () => {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [visiblePhotos, setVisiblePhotos] = useState<Photo[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [isGridView, setIsGridView] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const photosPerPage = 8;

  useEffect(() => {
    // Get all photos
    const allPhotos = getPhotos();
    setPhotos(allPhotos);
    
    // Initialize visible photos
    setVisiblePhotos(allPhotos.slice(0, photosPerPage));
  }, []);

  useEffect(() => {
    // Filter photos based on search query and status
    const filteredPhotos = photos.filter((photo) => {
      const matchesSearch =
        searchQuery === "" ||
        photo.customerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        photo.productName.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStatus =
        filterStatus === "all" || photo.status === filterStatus;

      return matchesSearch && matchesStatus;
    });

    // Reset visible photos when filters change
    setVisiblePhotos(filteredPhotos.slice(0, photosPerPage));
  }, [searchQuery, filterStatus, photos]);

  const handleLoadMore = () => {
    setIsLoading(true);
    
    // Simulate loading
    setTimeout(() => {
      // Get filtered photos
      const filteredPhotos = photos.filter((photo) => {
        const matchesSearch =
          searchQuery === "" ||
          photo.customerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          photo.productName.toLowerCase().includes(searchQuery.toLowerCase());

        const matchesStatus =
          filterStatus === "all" || photo.status === filterStatus;

        return matchesSearch && matchesStatus;
      });
      
      // Calculate next batch
      const currentLength = visiblePhotos.length;
      const nextPhotos = filteredPhotos.slice(
        currentLength,
        currentLength + photosPerPage
      );
      
      // Update visible photos
      setVisiblePhotos([...visiblePhotos, ...nextPhotos]);
      setIsLoading(false);
    }, 800);
  };

  const handleDownload = (id: string) => {
    const photo = photos.find((p) => p.id === id);
    if (photo) {
      downloadPhoto(photo);
    }
  };

  // Handler functions to match the new component prop structure
  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
  };

  const handleFilterChange = (filter: string) => {
    setFilterStatus(filter);
  };

  const handleViewChange = (isGrid: boolean) => {
    setIsGridView(isGrid);
  };

  return (
    <>
      <Navbar />
      
      <main className="min-h-screen py-32">
        <div className="container max-w-7xl">
          <div className="mb-10">
            <h1 className="text-3xl font-bold">Photo Gallery</h1>
            <p className="text-muted-foreground mt-2">
              Browse all submitted photos
            </p>
          </div>
          
          <SearchAndFilter
            searchQuery={searchQuery}
            onSearchChange={handleSearchChange}
            filterStatus={filterStatus}
            onFilterChange={handleFilterChange}
            isGridView={isGridView}
            onViewChange={handleViewChange}
          />
          
          <div className="mt-8">
            <PhotoGrid
              photos={visiblePhotos}
              isGridView={isGridView}
              onDownload={handleDownload}
            />
            
            {visiblePhotos.length > 0 &&
              visiblePhotos.length < 
              photos.filter(photo => {
                const matchesSearch =
                  searchQuery === "" ||
                  photo.customerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  photo.productName.toLowerCase().includes(searchQuery.toLowerCase());
                
                const matchesStatus =
                  filterStatus === "all" || photo.status === filterStatus;
                
                return matchesSearch && matchesStatus;
              }).length && (
                <LoadMoreButton
                  isLoading={isLoading}
                  onClick={handleLoadMore}
                />
              )}
          </div>
        </div>
      </main>
      
      <Footer />
    </>
  );
};

export default Gallery;
